import React from 'react';
import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';

import CurrencySign from 'components/price/CurrencySign.component';
import STORE from 'config/store.singleton';
import STAFF_NAME from 'constants/staff/name.const';
import fromAnyToString from 'utils/converters/fromAnyToString';
import isNilValue from 'utils/predicates/any/isNilValue.util';
import parsfixed from 'utils/values/parsfixed.util';

import InputFieldContainer from 'onboarding/common/InputField';
import D3 from 'reports/DashboardReports/D3';
import formatMoney from 'reports/formatMoney.util';
import fromMoneyStringToNumber from 'reports/fromMoneyStringToNumber.util';
import { sortArrayAccordingToAnotherArray } from 'reports/sortArrayAccordingToAnotherArray.util';

momentDurationFormatSetup(moment);

const fromPercentageToString = (num) => {
  if (Number.isNaN(num)) return '';

  const fixed = num.toFixed(1);
  const length = fixed.length;

  const string = fixed[length - 1] === '0' ? fixed.substring(0, length - 2) : fixed;

  return `${string}%`;
};

export const convertToMoneyAfterVat = (money) => {
  if (typeof money === 'string') money = parseFloat(money);
  const salonVatRate = window.salonVatRate ? window.salonVatRate : 0;
  return Math.round((money / (1 + salonVatRate / 100)) * 100) / 100;
};

const fromMinutesToHoursString = (num) => moment.duration(num, 'minutes').format('h [hours], m [min]');

/**
 * This function will make sure that all table cells that are 0, 0.0% or whatever 0 are set to "-"
 * @param {*} allRows
 */
export const clearTableRowsFrom0s = (allRows) => {
  allRows.forEach((row) => {
    row.inputFields.forEach((field, i) => {
      if (!field || field === '0' || field === '0%' || field === formatMoney(0)) {
        row.inputFields[i] = '-';
      }
    });
  });

  return allRows;
};

export const calculateWorkingHoursByCategory = (data) => {
  const categoriesMap = {};
  Object.keys(data.stylist_group).forEach((key) => {
    const stylist = data.stylist_group[key];
    stylist.categories &&
      stylist.categories.forEach((category) => {
        Object.keys(category).forEach((catKey) => {
          if (!categoriesMap[catKey]) {
            categoriesMap[catKey] = { hoursWorked: parseFloat(category[catKey]) };
          } else {
            categoriesMap[catKey] = { hoursWorked: categoriesMap[catKey].hoursWorked + parseFloat(category[catKey]) };
          }
        });
      });
  });

  Object.keys(categoriesMap).forEach((key) => {
    categoriesMap[key].hoursWorked = fromMinutesToHoursString(categoriesMap[key].hoursWorked);
  });

  return categoriesMap;
};

export const calculateWorkingHoursByService = (data) => {
  const servicesMap = {};
  Object.keys(data.stylist_group).forEach((key) => {
    const stylist = data.stylist_group[key];
    stylist.services &&
      stylist.services.forEach((category) => {
        Object.keys(category).forEach((catKey) => {
          if (!servicesMap[catKey]) {
            servicesMap[catKey] = { hoursWorked: parseFloat(category[catKey]) };
          } else {
            servicesMap[catKey] = { hoursWorked: servicesMap[catKey].hoursWorked + parseFloat(category[catKey]) };
          }
        });
      });
  });

  Object.keys(servicesMap).forEach((key) => {
    servicesMap[key].hoursWorked = fromMinutesToHoursString(servicesMap[key].hoursWorked);
  });

  return servicesMap;
};

export const convertStylistsMapsToStylistTableData = (
  nameServicesTotalPriceMap,
  nameProductsTotalPriceMap,
  currency,
) => {
  const keysToIterate = [];

  // Gets all the keys that are needed from the both maps
  Object.keys(nameServicesTotalPriceMap).forEach((key) => {
    if (keysToIterate.indexOf(key) === -1) {
      keysToIterate.push(key);
    }
  });

  Object.keys(nameProductsTotalPriceMap).forEach((key) => {
    if (keysToIterate.indexOf(key) === -1) {
      keysToIterate.push(key);
    }
  });

  // Defines the headers of the table
  const headers = {
    inputFields: [
      'Stylist',
      'Service revenue (including discounts)',
      '% of service revenue',
      'Service discounts given',
      '% of discounts given',
      'Product revenue (including discounts)',
      '% of product revenue',
      'Product discounts',
      '% of product discounts',
      'Total revenue (including discounts)',
      '% of total revenue',
      'Total discounts',
      '% of total discounts',
    ],
    styles: ['StylistHeaderRow'],
  };

  // Extract all the needed information depending on that if there
  // is an information for that specific key (stylist name)
  // If there is no information it is assumed that everything is 0
  const allRows = [];
  // These variable definitions are needed for the last row of information
  let totalServiceRevenue = 0;
  let totalServiceDiscounts = 0;
  let totalProductRevenue = 0;
  let totalProductDiscounts = 0;
  let totalRevenue = 0;
  let totalDiscounts = 0;

  keysToIterate.forEach((key) => {
    let serviceTotal = 0;
    let serviceDiscountTotal = 0;
    let productTotal = 0;
    let productDiscount = 0;

    const row = [key];
    if (nameServicesTotalPriceMap[key]) {
      serviceTotal = parseFloat(nameServicesTotalPriceMap[key].totalPrice);
      serviceDiscountTotal = parseFloat(nameServicesTotalPriceMap[key].totalDiscount);
      row.push(formatMoney(serviceTotal, currency));
      totalServiceRevenue += serviceTotal;
      row.push(0);
      row.push(formatMoney(serviceDiscountTotal, currency));
      totalServiceDiscounts += serviceDiscountTotal;
      row.push(0);
    } else {
      row.push(formatMoney(0, currency));
      row.push(0);
      row.push(formatMoney(0, currency));
      row.push(0);
    }

    if (nameProductsTotalPriceMap[key]) {
      productTotal = parseFloat(nameProductsTotalPriceMap[key].totalPrice);
      productDiscount = parseFloat(nameProductsTotalPriceMap[key].totalDiscount);
      row.push(formatMoney(productTotal, currency));
      totalProductRevenue += productTotal;
      row.push(0);
      row.push(formatMoney(productDiscount, currency));
      totalProductDiscounts += productDiscount;
      row.push(0);
    } else {
      row.push(formatMoney(0, currency));
      row.push(0);
      row.push(formatMoney(0, currency));
      row.push(0);
    }

    row.push(formatMoney(serviceTotal + productTotal), currency);
    totalRevenue += serviceTotal + productTotal;
    row.push(0);
    row.push(formatMoney(serviceDiscountTotal + productDiscount, currency));
    totalDiscounts += serviceDiscountTotal + productDiscount;
    row.push(0);
    allRows.push({ inputFields: row, styles: [] });
  });

  // Calculate the % depending on the totals
  allRows.forEach((row) => {
    row.inputFields[2] = !totalServiceRevenue
      ? 0
      : fromPercentageToString((fromMoneyStringToNumber(row.inputFields[1]) / totalServiceRevenue) * 100);
    row.inputFields[4] = !totalServiceDiscounts
      ? 0
      : fromPercentageToString((fromMoneyStringToNumber(row.inputFields[3]) / totalServiceDiscounts) * 100);
    row.inputFields[6] = !totalProductRevenue
      ? 0
      : fromPercentageToString((fromMoneyStringToNumber(row.inputFields[5]) / totalProductRevenue) * 100);
    row.inputFields[8] = !totalProductDiscounts
      ? 0
      : fromPercentageToString((fromMoneyStringToNumber(row.inputFields[7]) / totalProductDiscounts) * 100);
    row.inputFields[10] = !totalRevenue
      ? 0
      : fromPercentageToString((fromMoneyStringToNumber(row.inputFields[9]) / totalRevenue) * 100);
    row.inputFields[12] = !totalDiscounts
      ? 0
      : fromPercentageToString((fromMoneyStringToNumber(row.inputFields[11]) / totalDiscounts) * 100);
  });

  // Include the headers in the array
  allRows.splice(0, 0, headers);

  // Create the final row with the totals
  const finalRow = {
    inputFields: [
      'SalonTotal',
      !totalServiceRevenue ? 0 : formatMoney(totalServiceRevenue, currency),
      !totalServiceRevenue ? '' : '100%',
      !totalServiceDiscounts ? 0 : formatMoney(totalServiceDiscounts, currency),
      !totalServiceDiscounts ? '' : '100%',
      !totalProductRevenue ? 0 : formatMoney(totalProductRevenue, currency),
      !totalProductRevenue ? '' : '100%',
      !totalProductDiscounts ? 0 : formatMoney(totalProductDiscounts, currency),
      !totalProductDiscounts ? '' : '100%',
      !totalRevenue ? 0 : formatMoney(totalRevenue, currency),
      !totalRevenue ? '' : '100%',
      !totalDiscounts ? 0 : formatMoney(totalDiscounts, currency),
      !totalDiscounts ? '' : '100%',
    ],
    styles: ['StylistTotalRow'],
  };

  allRows.push(finalRow);

  return clearTableRowsFrom0s(allRows);
};

export const getRevenuSummary = (
  nameServicesTotalPriceMap,
  nameProductsTotalPriceMap,
  noShows,
  visits,
  transactions_count,
  currency,
) => {
  const keysToIterate = [];
  // Gets all the keys that are needed from the both maps
  Object.keys(nameServicesTotalPriceMap).forEach((key) => {
    if (keysToIterate.indexOf(key) === -1) {
      keysToIterate.push(key);
    }
  });
  Object.keys(nameProductsTotalPriceMap).forEach((key) => {
    if (keysToIterate.indexOf(key) === -1) {
      keysToIterate.push(key);
    }
  });

  // Defines the headers of the table
  const headers = {
    inputFields: [
      'Staff',
      'Service Revenue',
      // '% of service revenue',
      'Retail Revenue',
      // '% of product revenue',
      'No-show Fees',
      // '% of no-show fees',
      'Total Revenue',
      // '% of total revenue',
      'Clients checked out',
      'Average bill',
    ],
    styles: ['StylistHeaderRow'],
  };

  // Extract all the needed information depending on that if there
  // is an information for that specific key (stylist name)
  // If there is no information it is assumed that everything is 0
  const allRows = [];
  // These variable definitions are needed for the last row of information
  let totalServiceRevenue = 0;
  let totalProductRevenue = 0;
  let totalNoshowFee = 0;
  let totalRevenue = 0;

  keysToIterate.forEach((key) => {
    let serviceTotal = 0;
    let productTotal = 0;
    let noShowTotal = 0;

    const row = [key];
    if (nameServicesTotalPriceMap[key]) {
      serviceTotal = convertToMoneyAfterVat(parseFloat(nameServicesTotalPriceMap[key].totalPrice));
      row.push(formatMoney(serviceTotal, currency));
      totalServiceRevenue += serviceTotal;
    } else {
      row.push(formatMoney(0, currency));
    }

    if (nameProductsTotalPriceMap[key]) {
      productTotal = convertToMoneyAfterVat(parseFloat(nameProductsTotalPriceMap[key].totalPrice));
      row.push(formatMoney(productTotal, currency));
      totalProductRevenue += productTotal;
    } else {
      row.push(formatMoney(0, currency));
    }

    if (noShows && noShows[key]) {
      noShowTotal = noShows[key];
      row.push(formatMoney(convertToMoneyAfterVat(noShowTotal), currency));
      totalNoshowFee += noShowTotal;
    } else {
      row.push(formatMoney(0, currency));
    }

    row.push(formatMoney(serviceTotal + productTotal + noShowTotal, currency));
    totalRevenue += serviceTotal + productTotal + noShowTotal;

    if (visits && visits[key]) {
      // checked out clients
      row.push(visits[key].toString());
      row.push(formatMoney(parsfixed((serviceTotal + productTotal) / visits[key]), currency));
    } else {
      row.push('0');
      row.push(formatMoney(parsfixed(0), currency));
    }
    allRows.push({ inputFields: row, styles: [] });
  });

  // Include the headers in the array
  allRows.splice(0, 0, headers);
  const averageBill = transactions_count !== 0 ? totalRevenue / transactions_count : 0;
  // Create the final row with the totals
  const finalRow = {
    inputFields: [
      'Salon Total',
      !totalServiceRevenue ? 0 : formatMoney(totalServiceRevenue, currency),
      !totalProductRevenue ? 0 : formatMoney(totalProductRevenue, currency),
      !totalNoshowFee ? 0 : formatMoney(convertToMoneyAfterVat(totalNoshowFee), currency),
      !totalRevenue ? 0 : formatMoney(totalRevenue, currency),
      transactions_count ? transactions_count.toString() : '0',
      formatMoney(averageBill, currency),
    ],
    styles: ['StylistTotalRow'],
  };

  allRows.push(finalRow);
  return clearTableRowsFrom0s(allRows);
};

// Payment Split by Staff member: Total Revenue
export const convertPaymentTypeDataToCashCardSplitTableData = (stylists, serivesOrRetail, currency) => {
  // Define the headers for the table
  let headers;
  if (serivesOrRetail === 'total') {
    headers = {
      inputFields: [
        serivesOrRetail.toUpperCase(),
        'Cash',
        'Card in-store',
        'Card online (via SlickPay)',
        'Deposits redeemed',
        'Vouchers redeemed',
        'Other',
        'Revenue (excl. tips)',
        'Tips',
      ],
      styles: ['StylistTableHeader'],
    };
  } else if (serivesOrRetail === 'services') {
    headers = {
      inputFields: [
        serivesOrRetail.toUpperCase(),
        'Cash',
        'Card in-store',
        'Card online (via SlickPay)',
        'Deposits redeemed',
        'Vouchers redeemed',
        'Other',
        'Service Revenue (incl. tips)',
      ],
      styles: ['StylistTableHeader'],
    };
  } else if (serivesOrRetail === 'products') {
    headers = {
      inputFields: [
        serivesOrRetail.toUpperCase(),
        'Cash',
        'Card in-store',
        'Card online (via SlickPay)',
        'Deposits redeemed',
        'Vouchers redeemed',
        'Other',
        'Product Revenue',
      ],
      styles: ['StylistTableHeader'],
    };
  }

  const allRows = [];
  const totalForAllStylist = { cardInStore: 0, cash: 0, cardViaSlick: 0, deposit: 0, voucher: 0, other: 0, tip: 0 };

  let totalForAll = 0;
  // Create the rows for the payment types
  stylists.forEach((stylist) => {
    const row = [];
    const total = { cardInStore: 0, cash: 0, cardViaSlick: 0, deposit: 0, voucher: 0, other: 0, tip: 0 };

    let stylistTotal = 0;
    row.push(stylist.name);
    // how do we cast the return value of Object.entries correctly?
    // make the enries array as any time, inside forEach, cast each element as the type you want
    if (serivesOrRetail === 'services') {
      total.cash = convertToMoneyAfterVat(stylist.payment_types.services.cash);
      total.cardInStore = convertToMoneyAfterVat(stylist.payment_types.services.card);
      total.cardViaSlick = convertToMoneyAfterVat(stylist.payment_types.services.online);
      const deposit = stylist.payment_types.services.deposit;
      total.deposit = convertToMoneyAfterVat(deposit);
      const voucher = stylist.payment_types.services.voucher;
      total.voucher = convertToMoneyAfterVat(voucher);
      total.other = convertToMoneyAfterVat(stylist.payment_types.services.other);
      // total.tip = convertToMoneyAfterVat(parseFloat(stylist.tip));
    } else if (serivesOrRetail === 'products') {
      total.cash = convertToMoneyAfterVat(stylist.payment_types.products.cash);
      total.cardInStore = convertToMoneyAfterVat(stylist.payment_types.products.card);
      total.cardViaSlick = convertToMoneyAfterVat(stylist.payment_types.products.online);
      const deposit = stylist.payment_types.products.deposit;
      total.deposit = convertToMoneyAfterVat(deposit);
      const voucher = stylist.payment_types.products.voucher;
      total.voucher = convertToMoneyAfterVat(voucher);
      total.other = convertToMoneyAfterVat(stylist.payment_types.products.other);
      // total.tip = convertToMoneyAfterVat(parseFloat(stylist.tip));
    } else if (serivesOrRetail === 'total') {
      total.cash = convertToMoneyAfterVat(stylist.payment_types.services.cash + stylist.payment_types.products.cash);
      total.cardInStore = convertToMoneyAfterVat(
        stylist.payment_types.services.card + stylist.payment_types.products.card,
      );
      total.cardViaSlick = convertToMoneyAfterVat(
        stylist.payment_types.services.online + stylist.payment_types.products.online,
      );
      total.deposit = convertToMoneyAfterVat(
        parseFloat(stylist.payment_types.services.deposit) + parseFloat(stylist.payment_types.products.deposit),
      );
      total.voucher = convertToMoneyAfterVat(
        parseFloat(stylist.payment_types.services.voucher) + parseFloat(stylist.payment_types.products.voucher),
      );
      total.other = convertToMoneyAfterVat(stylist.payment_types.services.other + stylist.payment_types.products.other);
      total.tip = convertToMoneyAfterVat(parseFloat(stylist.tip));
    }
    row.push(formatMoney(parseFloat(total.cash), currency));
    row.push(formatMoney(parseFloat(total.cardInStore), currency));
    row.push(formatMoney(parseFloat(total.cardViaSlick), currency));
    row.push(formatMoney(parseFloat(total.deposit), currency));
    row.push(formatMoney(parseFloat(total.voucher), currency));
    row.push(formatMoney(parseFloat(total.other), currency));

    if (serivesOrRetail !== 'total') {
      stylistTotal = total.cash + total.cardInStore + total.cardViaSlick + total.other + total.deposit;
    } else {
      stylistTotal = convertToMoneyAfterVat(parseFloat(stylist.total_revenue));
    }
    totalForAllStylist.cash += parseFloat(total.cash);
    totalForAllStylist.cardInStore += parseFloat(total.cardInStore);
    totalForAllStylist.cardViaSlick += parseFloat(total.cardViaSlick);
    totalForAllStylist.deposit += parseFloat(total.deposit);
    totalForAllStylist.voucher += parseFloat(total.voucher);
    totalForAllStylist.other += parseFloat(total.other);

    totalForAll += stylistTotal;
    row.push(formatMoney(parseFloat(stylistTotal), currency));
    if (serivesOrRetail === 'total') {
      totalForAllStylist.tip += parseFloat(stylist.tip);
      row.push(formatMoney(parseFloat(stylist.tip), currency));
    }

    allRows.push({ inputFields: row, styles: [] });
  });

  const midRow = {
    inputFields: [`Total of ${serivesOrRetail}`],
    styles: ['StylistMiddleRow'],
  };
  // drawing footer
  midRow.inputFields.push(formatMoney(totalForAllStylist.cash, currency));
  midRow.inputFields.push(formatMoney(totalForAllStylist.cardInStore, currency));
  midRow.inputFields.push(formatMoney(totalForAllStylist.cardViaSlick, currency));
  midRow.inputFields.push(formatMoney(totalForAllStylist.deposit, currency));
  midRow.inputFields.push(formatMoney(totalForAllStylist.voucher, currency));
  midRow.inputFields.push(formatMoney(totalForAllStylist.other, currency));
  midRow.inputFields.push(formatMoney(totalForAll, currency));

  if (serivesOrRetail === 'total') {
    midRow.inputFields.push(formatMoney(totalForAllStylist.tip, currency));
  }

  allRows.splice(0, 0, headers);

  allRows.push(midRow);

  return clearTableRowsFrom0s(allRows);
};

export const cashCardSplitTotalTableData = (stylists, currency) => {
  const totalForAllStylist = { card: 0, cash: 0, deposit: 0, other: 0 };
  let totalForAll = 0;
  const allRows = [];
  // Create the rows for the payment types
  stylists.forEach((stylist) => {
    const row = [];
    const total = { card: 0, cash: 0, deposit: 0, other: 0 };
    let stylistTotal = 0;
    // row.push(stylist.name);

    // how do we cast the return value of Object.entries correctly?
    // make the enries array as any time, inside forEach, cast each element as the type you want
    const paymentTypeProduct = stylist.payment_types.products;
    Object.entries(paymentTypeProduct).forEach((type) => {
      total[type[0]] += parseFloat(type[1].total);
      totalForAllStylist[type[0]] += parseFloat(type[1].total);
    });
    const paymentTypeService = stylist.payment_types.services;
    Object.entries(paymentTypeService).forEach((type) => {
      total[type[0]] += parseFloat(type[1].total);
      totalForAllStylist[type[0]] += parseFloat(type[1].total);
      stylistTotal += total[type[0]];
    });
    totalForAll += stylistTotal;
    row.push(formatMoney(parseFloat(stylistTotal), currency));
  });

  const midRow = {
    inputFields: ['Total of Revenue'],
    styles: ['StylistMiddleRow'],
  };
  midRow.inputFields.push(formatMoney(totalForAllStylist.cash, currency));
  midRow.inputFields.push(formatMoney(totalForAllStylist.card, currency));
  midRow.inputFields.push(formatMoney(totalForAllStylist.deposit, currency));
  midRow.inputFields.push(formatMoney(totalForAllStylist.other, currency));
  midRow.inputFields.push(formatMoney(totalForAll, currency));
  // allRows.splice(0, 0, headers);

  allRows.push(midRow);

  return clearTableRowsFrom0s(allRows);
};

// tblnew Cash/Card Split by client
export const convertPaymentTypeDataToCashCardSplitClientTableData = (clients, currency) => {
  // Define the headers for the table
  const headers = {
    inputFields: [
      'Client',
      'Last Visit',
      'Cash',
      'Card in-store',
      'Card online (via SlickPay)',
      'Deposits redeemed',
      'Vouchers redeemed',
      'Other',
      'Revenue (excl. tips)',
      'Tips',
    ],
    styles: ['StylistTableHeader'],
  };

  const allRows = [];
  const totalForAllClient = {
    card: 0,
    cash: 0,
    online: 0,
    other: 0,
    depositRedeemed: 0,
    voucherRedeemed: 0,
    tip: 0,
  };
  let totalRevenu = 0;
  // Create the rows for the payment types
  clients.forEach((client) => {
    const row = [];
    const oneClient = {
      card: 0,
      cash: 0,
      online: 0,
      other: 0,
      depositRedeemed: 0,
      voucherRedeemed: 0,
      tip: 0,
    };
    oneClient.depositRedeemed = convertToMoneyAfterVat(client.payment_types.deposit.total);
    oneClient.voucherRedeemed = convertToMoneyAfterVat(client.payment_types.voucher.total);
    totalForAllClient.depositRedeemed += parseFloat(oneClient.depositRedeemed);
    totalForAllClient.voucherRedeemed += parseFloat(oneClient.voucherRedeemed);
    row.push(isNilValue(client.id) || client.id === 0 ? 'Walk In' : client.name);

    const date = moment(client.last_visit_date_time);
    const formattedDateTime = `${date.format('DD MMM YYYY')} at ${date.format('HH:mm')}`;
    row.push(formattedDateTime);
    // how do we cast the return value of Object.entries correctly?
    // make the enries array as any time, inside forEach, cast each element as the type you want
    const paymentType = client.payment_types;
    Object.entries(paymentType).forEach((type) => {
      if (type[0] !== 'deposit' && type[0] !== 'voucher') {
        // could be cash, card, online, other and tip
        const value = formatMoney(convertToMoneyAfterVat(parseFloat(type[1])), currency);

        if (type[0] === 'cash') {
          row[2] = value;
        }
        if (type[0] === 'card') {
          row[3] = value;
        }
        if (type[0] === 'online') {
          row[4] = value;
        }
        if (type[0] === 'other') {
          row[7] = value;
        }
        if (type[0] === 'tip') {
          row[9] = value;
        }
        // column SlickPay via stripe and column Other
        oneClient[type[0]] += convertToMoneyAfterVat(parseFloat(type[1]));
        totalForAllClient[type[0]] += convertToMoneyAfterVat(parseFloat(type[1]));
      }
    });
    totalRevenu =
      oneClient.cash +
      oneClient.card +
      oneClient.online +
      oneClient.other +
      oneClient.voucherRedeemed +
      oneClient.depositRedeemed -
      client.tip;
    row[5] = formatMoney(parseFloat(oneClient.depositRedeemed), currency);
    row[6] = formatMoney(parseFloat(oneClient.voucherRedeemed), currency);
    row[8] = formatMoney(parseFloat(totalRevenu), currency);
    row[9] = formatMoney(convertToMoneyAfterVat(parseFloat(client.tip)), currency);

    allRows.push({ inputFields: row, styles: [] });
  });

  const midRow = {
    inputFields: ['Total'],
    styles: ['StylistMiddleRow'],
  };

  // drawing footer
  const totalRevenuForAll =
    totalForAllClient.cash +
    totalForAllClient.card +
    totalForAllClient.online +
    totalForAllClient.other +
    totalForAllClient.depositRedeemed +
    totalForAllClient.voucherRedeemed;
  midRow.inputFields.push('-');
  midRow.inputFields.push(formatMoney(totalForAllClient.cash, currency));
  midRow.inputFields.push(formatMoney(totalForAllClient.card, currency));
  midRow.inputFields.push(formatMoney(totalForAllClient.online, currency));
  midRow.inputFields.push(formatMoney(totalForAllClient.depositRedeemed, currency));
  midRow.inputFields.push(formatMoney(totalForAllClient.voucherRedeemed, currency));
  midRow.inputFields.push(formatMoney(totalForAllClient.other, currency));
  midRow.inputFields.push(formatMoney(totalRevenuForAll, currency));
  midRow.inputFields.push(formatMoney(totalForAllClient.tip, currency));

  allRows.splice(0, 0, headers);
  allRows.push(midRow);
  return clearTableRowsFrom0s(allRows);
};

/**
 * this function will group the stylists' data and will create a map
 * with type key: stylistName and value {categories:[], services:[], products:[]}
 *
 * @export
 * @param {SalesStylistGroupData} data
 */
export const groupStylistCategoriesServicesAndProducts = (data) => {
  const combinedStylistsMap = {};

  data.forEach((stylist) => {
    combinedStylistsMap[stylist.name] = {};
    combinedStylistsMap[stylist.name].categories = stylist.categories || [];
    combinedStylistsMap[stylist.name].services = stylist.services || [];
    combinedStylistsMap[stylist.name].products = stylist.products;
    combinedStylistsMap[stylist.name].revenue_from_no_shows = stylist.revenue_from_no_shows;
  });

  return combinedStylistsMap;
};

/**
 * This function adds the commission percentages to the currect combined stylist's object created by the above function.
 * Then using that object the table's rows and are being created.
 * @param {*} combinedStylistObj
 * @param {*} data
 */
export const addonCombinedStylistObjectWithCommisions = (combinedStylistObj, data) => {
  const dictionary = {
    service: 'services',
    category: 'categories',
    product: 'products',
    all_products: 'all_products',
    no_show: 'no_show',
  };
  data.forEach((commissionData) => {
    if (commissionData.object_type !== 'all_products' && commissionData.object_type !== 'no_show') {
      const groupItem = combinedStylistObj[dictionary[commissionData.object_type]][commissionData.object_id];
      if (groupItem) {
        groupItem.commission = commissionData;
      }
    } else if (commissionData.object_type === 'all_products') {
      combinedStylistObj.products.combinedCommission = commissionData;
    } /* else if (commissionData.object_type === 'no_show' && combinedStylistObj.noshowCommission) {
      combinedStylistObj.noshowCommission = commissionData;
    } else if (commissionData.object_type === 'no_show' && !combinedStylistObj.noshowCommission) {
      combinedStylistObj.noshowCommission = 0;
    } */
  });
};

export const convertGroupedStylistDataToCategoriesTable = (
  stylistData,
  stylistName,
  onBlur,
  globalDiscount,
  totalProductCommission,
  // currency TODO:
) => {
  // prepare the headers
  const allProductCommission = stylistData.products.combinedCommission
    ? stylistData.products.combinedCommission.id
    : -1;
  const noshowCommissionId = stylistData.noshowId ? stylistData.noshowId : -1;
  const headers = {
    inputFields: [
      'Categories',
      'Total revenue (including discounts)',
      '% of total revenue',
      'Discounts given',
      '% discounts',
      'Commission Rate %',
      'Commission',
    ],
    styles: ['a1'],
  };
  const allRows = [];

  // calculates the categories rows
  let totalCategoriesRevenue = 0;
  let totalNoShowRevenue = 0;
  let totalCategoriesDiscount = 0;
  let totalCategoriesCommission = 0;
  Object.keys(stylistData.categories).forEach((key) => {
    const row = [];
    const price = parseFloat(stylistData.categories[key].price);
    const noShow = parseFloat(stylistData.revenue_from_no_shows);
    const commissionPercentage =
      (stylistData.categories[key].commission &&
        +(stylistData.categories[key].commission.percentage * 100).toFixed(2)) ||
      0;
    const commission = price * (commissionPercentage / 100 / (1 + globalDiscount));
    row.push(stylistData.categories[key].name);
    row.push(formatMoney(convertToMoneyAfterVat(price)));

    totalCategoriesRevenue += price;
    totalNoShowRevenue += noShow;
    row.push(0);
    row.push(formatMoney(stylistData.categories[key].discount));
    totalCategoriesDiscount += parseFloat(stylistData.categories[key].discount);
    row.push(0);
    row.push(
      price ? (
        <InputFieldContainer
          data={{
            id: `${key}`,
            type: 'text',
            inputValue: `${commissionPercentage}`,
            placeholder: '',
            label: '',
            inputMode: 'text',
          }}
          onBlurHandler={(v) => {
            let commissionData = stylistData.categories[key].commission;
            const perc = +v / 100;
            if (!commissionData) {
              // guan the styist below need to change
              commissionData = { id: -1, object_type: 'category', object_id: +key, percentage: perc, stylist: 1 };
            }
            onBlur(commissionData, perc);
          }}
          errorMessage={stylistData.categories[key].commission && stylistData.categories[key].commission.error}
          tableInputField
        />
      ) : (
        0
      ),
    );
    row.push(parseFloat(convertToMoneyAfterVat(commission)).toFixed(2));
    totalCategoriesCommission += commission;
    allRows.push({ inputFields: row, styles: [] });
  });

  // prepare the mid-row
  const midRow = [
    'Total Services',
    formatMoney(convertToMoneyAfterVat(totalCategoriesRevenue)),
    0,
    formatMoney(convertToMoneyAfterVat(totalCategoriesDiscount)),
    0,
    0,
    formatMoney(convertToMoneyAfterVat(totalCategoriesCommission)),
  ];

  // calculate the retail row
  let totalRetailRevenue = 0;
  let totalRetailDiscount = 0;
  const combinedCommission = {};
  Object.keys(stylistData.products).forEach((key) => {
    // if the key is not an actual product but the combined commission, just return
    if (key === 'combinedCommission') {
      Object.assign(combinedCommission, stylistData.products[key]);
      return;
    }
    totalRetailRevenue += stylistData.products[key].price;
    totalRetailDiscount += stylistData.products[key].discount;
  });
  const commissionRetail = stylistData.products.combinedCommission
    ? stylistData.products.combinedCommission.percentage
    : 0;
  const commissionNoshow = stylistData.noshowCommission ? stylistData.noshowCommission : 0;
  const retailRow = [
    'Retail',
    formatMoney(convertToMoneyAfterVat(totalRetailRevenue)),
    0,
    formatMoney(convertToMoneyAfterVat(totalRetailDiscount)),
    0,
    <InputFieldContainer
      data={{
        id: 'commissionRetail',
        type: 'text',
        inputValue: `${commissionRetail * 100}`,
        placeholder: '',
        label: '',
        inputMode: 'text',
      }}
      onBlurHandler={(v) => {
        const perc = +v / 100;
        const commissionData = {
          id: allProductCommission,
          object_type: 'all_products',
          object_id: 0,
          percentage: perc,
          stylist: 1,
        };
        onBlur(commissionData, perc);
      }}
      errorMessage=""
      tableInputField
    />,
    0,
    0,
  ];

  // apply global discount to retail GS-3837
  retailRow[6] = formatMoney(parseFloat(totalProductCommission));
  // calculate the %
  const commissionRate = commissionNoshow * 100;
  const noShowRow = [
    'No-show fees',
    formatMoney(convertToMoneyAfterVat(totalNoShowRevenue)),
    0,
    0,
    0,
    <InputFieldContainer
      data={{
        id: 'commissionNoshow',
        type: 'text',
        inputValue: `${commissionRate}`,
        placeholder: '',
        label: '',
        inputMode: 'text',
      }}
      onBlurHandler={(v) => {
        const perc = +v / 100;
        const commissionData = {
          id: noshowCommissionId,
          object_type: 'no_show',
          object_id: 0,
          percentage: perc,
          stylist: 1,
        };
        onBlur(commissionData, perc);
      }}
      errorMessage=""
      tableInputField
    />,
    0,
  ];

  // first % for the categories data
  const totalRevenue = totalCategoriesRevenue + totalRetailRevenue;
  const totalDiscount = totalCategoriesDiscount + totalRetailDiscount;

  let totalCategoryRevenuePercentage = 0;
  let totalCategoryDiscountPercentage = 0;
  allRows.forEach((row) => {
    const revenuePerc = !totalRevenue ? 0 : (fromMoneyStringToNumber(row.inputFields[1]) / totalRevenue) * 100;
    row.inputFields[2] = fromPercentageToString(revenuePerc);
    totalCategoryRevenuePercentage += revenuePerc;

    const discountPerc = !totalRevenue
      ? 0
      : (fromMoneyStringToNumber(row.inputFields[3]) /
          (fromMoneyStringToNumber(row.inputFields[1]) + fromMoneyStringToNumber(row.inputFields[3]))) *
        100;
    row.inputFields[4] = fromPercentageToString(discountPerc);
    totalCategoryDiscountPercentage += discountPerc;
  });

  // Then % for the mid row data
  midRow[2] = fromPercentageToString(totalCategoryRevenuePercentage);
  midRow[4] = fromPercentageToString(totalCategoryDiscountPercentage);
  allRows.push({ inputFields: midRow, styles: [] });

  // Then % for the Retail row
  retailRow[2] = !totalRevenue
    ? 0
    : fromPercentageToString((fromMoneyStringToNumber(retailRow[1]) / totalRevenue) * 100);
  retailRow[4] = !totalDiscount
    ? 0
    : fromPercentageToString(
        (fromMoneyStringToNumber(retailRow[3]) /
          (fromMoneyStringToNumber(retailRow[3]) + fromMoneyStringToNumber(retailRow[1]))) *
          100,
      );

  allRows.push({ inputFields: retailRow, styles: [] });
  allRows.push({ inputFields: noShowRow, styles: [] });
  totalCategoriesCommission = parseFloat(totalCategoriesCommission) + parseFloat(totalProductCommission);
  const finalRow = {
    inputFields: [
      `${stylistName} Total`,
      formatMoney(convertToMoneyAfterVat(totalRevenue)),
      !totalRevenue ? 0 : '100%',
      formatMoney(totalDiscount),
      !totalDiscount ? 0 : fromPercentageToString((totalDiscount / (totalDiscount + totalRevenue)) * 100),
      0,
      formatMoney(convertToMoneyAfterVat(totalCategoriesCommission)),
    ],
    styles: ['StylistTotalRow'],
  };

  // Finally add the header row and the final row
  allRows.splice(0, 0, headers);
  allRows.push(finalRow);

  return clearTableRowsFrom0s(allRows);
};

export const convertServicesOrProductsToTableData = (stylistData, firstHeader, onBlur, globalDiscount, currency) => {
  // prepare headers
  const headers = {
    inputFields: [
      firstHeader,
      'Total revenue (including discounts)',
      '% of total revenue',
      'Discounts given',
      '% discounts',
      'Commission Rate %',
      'Commission',
    ],
    styles: ['a3'],
  };
  const allRows = [];

  // Get the main rows
  const objectToUse = firstHeader === 'Services' ? stylistData.services : stylistData.products;
  let totalRevenue = 0;
  let totalDiscounts = 0;
  let totalCommission = 0;
  const productsCombinedPercentage =
    firstHeader === 'Retail Products' && objectToUse.combinedCommission
      ? +(objectToUse.combinedCommission.percentage * 100).toFixed(2)
      : 0;

  Object.keys(objectToUse).forEach((key) => {
    // if the key is not an actual product but the combined commission, just return
    if (key === 'combinedCommission') {
      return;
    }
    const category = objectToUse[key];
    const row = [];
    const price = parseFloat(category.price);
    const commissionPercentage =
      (objectToUse[key].commission && +(objectToUse[key].commission.percentage * 100).toFixed(2)) || 0;
    const commission = price * (commissionPercentage / 100 / (1 + globalDiscount));

    row.push(category.name);
    row.push(formatMoney(convertToMoneyAfterVat(price), currency));
    totalRevenue += price;
    row.push(0);
    row.push(formatMoney(convertToMoneyAfterVat(category.discount), currency));
    totalDiscounts += category.discount;
    row.push(0);
    row.push(
      // if there is combined value or we don't have a price, we don't need the product by product value
      productsCombinedPercentage || !price ? (
        0
      ) : (
        <InputFieldContainer
          data={{
            id: `${key}`,
            type: 'text',
            inputValue: `${commissionPercentage}`,
            placeholder: '',
            label: '',
            inputMode: 'text',
          }}
          onBlurHandler={(v) => {
            let commissionData = objectToUse[key].commission;
            if (!commissionData) {
              commissionData = {
                id: -1,
                object_type: firstHeader === 'Services' ? 'service' : 'product',
                object_id: +key,
                percentage: +v / 100,
              };
            }
            onBlur(commissionData, +v / 100);
          }}
          errorMessage={objectToUse[key].commission && objectToUse[key].commission.error}
          tableInputField
        />
      ),
    );
    row.push(productsCombinedPercentage ? 0 : convertToMoneyAfterVat(commission).toFixed(2));
    totalCommission += commission;

    allRows.push({ inputFields: row, styles: [] });
  });

  // Calculate the %
  allRows.forEach((row) => {
    row.inputFields[2] = !totalRevenue
      ? 0
      : fromPercentageToString((fromMoneyStringToNumber(row.inputFields[1]) / totalRevenue) * 100);
    row.inputFields[4] = !totalDiscounts
      ? 0
      : fromPercentageToString(
          (fromMoneyStringToNumber(row.inputFields[3]) /
            (fromMoneyStringToNumber(row.inputFields[3]) + fromMoneyStringToNumber(row.inputFields[1]))) *
            100,
        );
  });

  const finalRow = {
    inputFields: [
      `Total ${firstHeader}`,
      formatMoney(convertToMoneyAfterVat(totalRevenue), currency),
      !totalRevenue ? '' : '100%',
      formatMoney(convertToMoneyAfterVat(totalDiscounts), currency),
      !totalDiscounts ? '' : fromPercentageToString((totalDiscounts / (totalDiscounts + totalRevenue)) * 100),
      firstHeader === 'Services' ? (
        0
      ) : (
        <InputFieldContainer
          data={{
            id: `${firstHeader}_commission`,
            type: 'text',
            inputValue: `${productsCombinedPercentage || 0}`,
            placeholder: '',
            label: '',
            inputMode: 'text',
          }}
          onBlurHandler={(v) => {
            let commissionData = objectToUse.combinedCommission;
            const perc = +v / 100;
            if (!commissionData) {
              commissionData = {
                id: -1,
                object_type: 'all_products',
                object_id: 0,
                percentage: perc,
              };
            }
            onBlur(commissionData, perc);
          }}
          errorMessage={objectToUse.combinedCommission && objectToUse.combinedCommission.error}
          tableInputField
        />
      ),
      productsCombinedPercentage
        ? formatMoney(
            convertToMoneyAfterVat(
              ((productsCombinedPercentage / 100) * totalRevenue) / (1 + globalDiscount),
              currency,
            ),
          )
        : formatMoney(convertToMoneyAfterVat(totalCommission, currency)),
    ],
    styles: ['StylistTotalRow'],
  };

  // saveTotalProductCommission(parseFloat(finalRow.inputFields[6].substr(1)));
  allRows.splice(0, 0, headers);
  allRows.push(finalRow);

  return clearTableRowsFrom0s(allRows);
};

export const convertRebookingReportTypeToTable = (data) => {
  const headers = {
    inputFields: ['Stylist Name', 'Rebooked Appointments', 'Total Appointments', '% Rebooked'],
    styles: ['a3'],
  };
  const allRows = [headers];

  data.stylists &&
    Object.keys(data.stylists).forEach((key) => {
      const entity = data.stylists[key];
      const row = [];
      row.push(entity.name);
      row.push(entity.rebooked);
      row.push(entity.total);
      row.push(fromPercentageToString((entity.rebooked / entity.total) * 100));

      allRows.push({ inputFields: row, styles: [] });
    });

  const finalRow = {
    inputFields: [
      'Total',
      data.rebooked_appointments,
      data.total_appointments,
      fromPercentageToString(data.percentage * 100),
    ],
    styles: ['StylistTotalRow'],
  };
  allRows.push(finalRow);

  return clearTableRowsFrom0s(allRows);
};

export const countOfServicesToTable = (stylists) => {
  const headers = {
    inputFields: ['Stylist'],
    styles: ['a3'],
  };
  const tableField = [];
  stylists.forEach((stylist) => {
    const categories = Object.values(stylist.categories);
    categories.forEach((c) => tableField.push(c.name));
  });
  const uniqTableHeader = new Set(tableField);

  [...uniqTableHeader].map((h) => headers.inputFields.push(h));
  const allRows = stylists.length ? [headers] : [];
  stylists &&
    stylists.forEach((stylist) => {
      const row = [];
      const categories = Object.values(stylist.categories);
      row.push(stylist.name);
      for (let i = 0; i < headers.inputFields.length; i++) {
        categories.forEach((c) => {
          if (c.name === headers.inputFields[i]) row.push(c.visits);
        });
        if (row.length !== i + 1) {
          row.push('');
        }
      }
      allRows.push({ inputFields: row, styles: [] });
    });

  return clearTableRowsFrom0s(allRows);
};

export const convertStylistCategoryToCategoryRevenueTableData = (dataCategory, dataProducts, stylists, currency) => {
  const headers = {
    inputFields: ['Staff'],
    styles: ['a3'],
  };
  const tableField = [];
  const totalAll = [];
  let totalRevenue = 0;
  let totalRetail = 0;
  let stylistTotal = 0;
  let salonTotal = 0;
  let salonTotalRetail = 0;
  let totalNoShow = 0;
  let noShowTotal = 0;
  stylists.forEach((stylist) => {
    const categories = Object.values(stylist.categories);
    categories.forEach((c) => {
      tableField.push(c.name);
      return null;
    });
  });
  const uniqTableHeader = new Set(tableField);
  // get rid of the duplicated table field
  [...uniqTableHeader].map((h) => headers.inputFields.push(h));

  headers.inputFields.push('Retail');
  headers.inputFields.push('No-show fees');
  headers.inputFields.push('Total');
  const allRows = stylists.length ? [headers] : [];
  stylists &&
    stylists.forEach((stylist) => {
      const row = [];
      const revenue = {};
      const categories = Object.values(stylist.categories);
      row.push(stylist.name);
      for (let i = 1; i < headers.inputFields.length; i++) {
        categories.forEach((c) => {
          if (c.name === headers.inputFields[i]) {
            row.push(formatMoney(convertToMoneyAfterVat(c.price), currency));
          }
        });
        if (
          row.length - 1 < i &&
          i < headers.inputFields.length - 1 &&
          headers.inputFields[i] !== 'Retail' &&
          headers.inputFields[i] !== 'No-show fees'
        ) {
          row.push('');
        }
      }

      row.push(formatMoney(parsfixed(convertToMoneyAfterVat(parseFloat(stylist.revenue_from_products))), currency));

      totalNoShow = parseFloat(stylist.revenue_from_no_shows);
      totalRetail = parseFloat(stylist.revenue_from_products);
      salonTotalRetail += parseFloat(stylist.revenue_from_products);

      const revenueSummary = categories.map((c) => c.price);
      categories.map((c) => (revenue[c.name] = convertToMoneyAfterVat(c.price)));
      if (revenueSummary.length) {
        stylistTotal = revenueSummary.reduce((t, v) => {
          t += v;
          return t;
        });
      } else {
        stylistTotal = 0;
      }

      noShowTotal += totalNoShow;
      stylistTotal += totalRetail + totalNoShow;
      salonTotal += stylistTotal;

      row.push(formatMoney(convertToMoneyAfterVat(totalNoShow), currency));
      row.push(formatMoney(convertToMoneyAfterVat(stylistTotal), currency));

      allRows.push({ inputFields: row, styles: [] });
      totalAll.push(revenue);
    });

  const finalRow = {
    inputFields: ['Total'],
    styles: ['StylistMiddleRow'],
  };

  for (let i = 1; i < headers.inputFields.length; i += 1) {
    const catArr = dataCategory.filter((c) => c.name === headers.inputFields[i]);
    if (catArr.length > 0) {
      finalRow.inputFields.push(formatMoney(convertToMoneyAfterVat(parseFloat(catArr[0].total)), currency));
    }
  }
  totalRevenue += salonTotal;

  finalRow.inputFields.push(formatMoney(convertToMoneyAfterVat(salonTotalRetail), currency));
  finalRow.inputFields.push(formatMoney(convertToMoneyAfterVat(noShowTotal), currency));
  finalRow.inputFields.push(formatMoney(convertToMoneyAfterVat(totalRevenue), currency));

  if (stylists.length) {
    allRows.push(finalRow);
  }

  return clearTableRowsFrom0s(allRows);
};

export const convertHoursByCategoryOrServiceDataToTable = (data, type) => {
  const headers = {
    inputFields: [type, 'Hours Busy'],
    styles: ['a3'],
  };
  const allRows = [headers];

  Object.keys(data).forEach((key) => {
    const row = [];
    const entity = data[key];
    row.push(key);
    row.push(entity.hoursWorked);

    allRows.push({ inputFields: row, styles: [] });
  });

  return clearTableRowsFrom0s(allRows);
};

export const convertHoursStylistGroupDataToTable = (data, type) => {
  const headers = {
    inputFields: [type, 'Hours busy'],
    styles: ['a3'],
  };
  const allRows = [headers];
  const arrToUse = type === 'Category' ? data.categories : data.services;
  if (arrToUse) {
    arrToUse.forEach((item) => {
      const row = [];
      Object.keys(item).forEach((key) => {
        row.push(key);
        row.push(fromMinutesToHoursString(parseFloat(item[key])));
      });
      allRows.push({ inputFields: row, styles: [] });
    });
  }

  return clearTableRowsFrom0s(allRows);
};

export const convertNamedGroupDataToTableHistoric = (data, type, currency) => {
  const headers = {
    inputFields: [type, 'Historic Revenue', '% of Historic Revenue'],
    styles: ['a3'],
  };
  let sumTotal = 0;
  const allRows = [headers];
  Object.keys(data).forEach((key) => {
    const entity = data[parseInt(key, 10)];
    sumTotal += parseFloat(entity.total);
  });

  Object.keys(data).forEach((key) => {
    const entity = data[parseInt(key, 10)];

    const row = [];
    row.push(entity.name);
    row.push(formatMoney(entity.total, currency));
    row.push(fromPercentageToString((entity.total / sumTotal) * 100));
    allRows.push({ inputFields: row, styles: [] });
  });

  const finalRow = {
    inputFields: [`Total ${type}`, formatMoney(sumTotal, currency), sumTotal ? '100%' : ''],
    styles: ['StylistTotalRow'],
  };

  allRows.push(finalRow);

  return clearTableRowsFrom0s(allRows);
};

export const convertOriginGroupAppointmentsToTable = (data, currency) => {
  const headers = {
    inputFields: ['Origin', 'Number', 'Booking Value'],
    styles: ['a3'],
  };
  const allRows = [headers];
  const keysAndTitles = [
    { key: 'SALON_CREATED', title: 'Salon Created' },
    { key: 'ONLINE_BOOKING', title: 'Online Booking' },
    /* { key: 'ONLINE_REBOOKING', title: 'Online Rebooking' },
    { key: 'QUICK_SETUP', title: 'Quick Setup' }, */
  ];
  let totalNumber = 0;
  let totalValue = 0;
  keysAndTitles.forEach((entity) => {
    const row = [];
    const count = data[entity.key] ? data[entity.key].count : 0;
    totalNumber += count;
    const total = data[entity.key] ? convertToMoneyAfterVat(data[entity.key].total) : 0;
    totalValue += total;
    row.push(entity.title);
    row.push(count);
    row.push(formatMoney(total, currency));
    allRows.push({ inputFields: row, styles: [] });
  });
  allRows.push({ inputFields: ['Total', totalNumber, formatMoney(totalValue, currency)], styles: [] });
  return clearTableRowsFrom0s(allRows);
};

const KEY_REPLACEMENT_MAP = Object.freeze({
  SMS: 'Slick SMS Marketing',
  DIRECT: 'Website / Other',
  FACEBOOK: 'Facebook',
  INSTAGRAM: 'Instagram',
});

const utmGroupTitle = (key) => KEY_REPLACEMENT_MAP[key] || key;
const countFrom = (val) => (val && val.count) || 0;

export const convertUtmGroupAppointmentsToTable = (data) => [
  {
    inputFields: ['Online booking source', 'Number of bookings'],
    styles: ['a3'],
  },
  ...Object.entries(data)
    .map(([key, val]) => [utmGroupTitle(key), fromAnyToString(countFrom(val))])
    .map((inputFields) => ({ inputFields, styles: [] })),
  {
    inputFields: ['Total', fromAnyToString(Object.values(data).reduce((sum, val) => sum + countFrom(val), 0))],
    styles: [],
  },
];

export const convertStatusGroupAppointmentsToTable = (data, currency) => {
  const headers = {
    inputFields: ['Status', 'Number', 'Booking Value'],
    styles: ['a3'],
  };
  const allRows = [headers];
  const keysAndTitles = [
    { key: 'UNCONFIRMED', title: 'Unconfirmed' },
    { key: 'CONFIRMED', title: 'Confirmed' },
    { key: 'PAID', title: 'Paid' },
    { key: 'FINISHED', title: 'Finished' },
    { key: 'ARRIVED', title: 'Arrived' },
  ];
  keysAndTitles.forEach((entity) => {
    const row = [];
    const count = data[entity.key] ? data[entity.key].count : 0;
    const total = data[entity.key] ? convertToMoneyAfterVat(data[entity.key].total) : 0;
    row.push(entity.title);
    row.push(count);
    row.push(formatMoney(total, currency));
    allRows.push({ inputFields: row, styles: [] });
  });

  return clearTableRowsFrom0s(allRows);
};

export const convertV1toV2Table = (data) => {
  const converted = {
    rows: [],
  };

  if (!data.rows.length) {
    return converted;
  }

  const leftHeader = {
    inputFields: data.rows[0].inputFields[0] ? [data.rows[0].inputFields[0]] : [],
    styles: data.rows[0].styles[0] ? [data.rows[0].styles[0]] : [],
  };
  converted.topHeader = data.rows[0];
  if (converted.topHeader) {
    converted.topHeader.inputFields = converted.topHeader.inputFields.slice(1);
  }
  converted.rows = data.rows.slice(1);
  converted.rows = converted.rows.map((row) => {
    leftHeader.inputFields.push(row.inputFields[0]);
    leftHeader.styles.push(row.styles[0]);

    return {
      inputFields: row.inputFields.slice(1),
      styles: row.styles.slice(0, -1),
      metaData: row.metaData,
    };
  });
  converted.leftHeader = leftHeader;
  return converted;
};

export const generateRetailPerformanceTable = (
  revenueTableData,
  stylistData,
  uniqueClientsReport,
  unique_client_visits,
  currency,
) => {
  const retailPerformanceTable = {
    rows: [],
    leftHeader: {
      inputFields: ['Staff'],
      styles: [],
    },
    topHeader: {
      inputFields: [
        'Items Sold',
        'Value of retail sales',
        'Retail % of total Sales',
        'Unique Client Visits',
        'Clients who bought retail',
        'Retail % attach rate',
      ],
      styles: [],
    },
  };

  let totalProductsCount = 0;
  let totalProductRevenue = 0;
  let totalClients = 0;
  let totalUniqClientVisits = 0;

  stylistData.forEach((stylist) => {
    retailPerformanceTable.leftHeader && retailPerformanceTable.leftHeader.inputFields.push(stylist.name);
    const rowToAdd = {
      inputFields: [],
      styles: [],
    };

    rowToAdd.inputFields.push(stylist.products_sold_count.toString());
    totalProductsCount += stylist.products_sold_count;
    // Value of retail sales
    rowToAdd.inputFields.push(formatMoney(convertToMoneyAfterVat(stylist.revenue_from_products), currency));
    totalProductRevenue += convertToMoneyAfterVat(stylist.revenue_from_products);

    // 3rd column retail %
    const revenueRowForStylist = revenueTableData.find((row) => row.inputFields[0] === stylist.name);
    if (revenueRowForStylist) {
      // get the cell that is the "Total Revenue"
      // rowToAdd.inputFields.push(revenueRowForStylist.inputFields[9]);
      const percent =
        revenueRowForStylist.inputFields[9] === '-'
          ? 0
          : Math.round(
              (stylist.revenue_from_products /
                parseFloat(revenueRowForStylist.inputFields[9].substr(1).replace(',', ''))) *
                100,
            );
      rowToAdd.inputFields.push(`${percent}%`);
    } else {
      rowToAdd.inputFields.push('0%');
    }

    // 4th column uniq client Visits
    rowToAdd.inputFields.push(stylist.visits.toString());
    totalUniqClientVisits += stylist.visits;
    // try to find unique clients data for the stylist
    if (uniqueClientsReport) {
      const uniqueClientForStylist = uniqueClientsReport.find((st) => st.stylistId === stylist.id);

      // 5th column Clents who bought retail
      if (uniqueClientForStylist) {
        rowToAdd.inputFields.push(uniqueClientForStylist.unique_clients.toString());
        totalClients += uniqueClientForStylist.unique_clients;
      } else {
        rowToAdd.inputFields.push('0');
      }

      // 6th retail attach
      if (uniqueClientForStylist && stylist.visits !== 0) {
        rowToAdd.inputFields.push(
          `${parseFloat((uniqueClientForStylist.unique_clients / stylist.visits) * 100).toFixed(0)}%`,
        );
      } else {
        rowToAdd.inputFields.push('0%');
      }
    }
    retailPerformanceTable.rows.push(rowToAdd);
  });

  // Prepare the final row
  retailPerformanceTable.leftHeader && retailPerformanceTable.leftHeader.inputFields.push('Salon Total');
  const totalRevenue = convertToMoneyAfterVat(
    parseFloat(revenueTableData[revenueTableData.length - 1].inputFields[9].substr(1).replace(',', '')),
  );
  const totalPercent =
    totalRevenue !== 0 && totalProductRevenue !== 0
      ? `${Math.round((totalProductRevenue / totalRevenue) * 100)}%`
      : '0';

  const totalRetailAttach =
    totalUniqClientVisits !== 0 ? `${parseFloat((totalClients / totalUniqClientVisits) * 100).toFixed(0)}%` : '0%';
  const finalRow = {
    inputFields: [
      totalProductsCount.toString(),
      formatMoney(totalProductRevenue, currency), // Total Revenue from products,
      // revenueTableData[revenueTableData.length - 1].inputFields[9], // total sales
      totalPercent,
      unique_client_visits ? unique_client_visits.toString() : '0',
      totalClients ? totalClients.toString() : '0',
      totalRetailAttach,
    ],
    styles: [],
  };
  retailPerformanceTable.rows.push(finalRow);
  return retailPerformanceTable;
};

export const generateClientPerformanceTable = (
  uniqueVisitsReport,
  rebookingReport,
  visitFrequencyReport,
  stylistRequests,
  stylistList,
) => {
  const perStylistData = {};
  const requestsArr = stylistRequests.lastRequestResponse;

  uniqueVisitsReport.by_stylist.forEach((stylist) => {
    perStylistData[stylist.id] = {
      name: stylist.name,
      visits: stylist.visits_count,
      rebooking: 0,
      frequency: 0,
    };

    const rebookingData = rebookingReport.stylists[stylist.id.toString()];

    if (rebookingData) {
      perStylistData[stylist.id].total = rebookingData.unique_visits; // total;
      perStylistData[stylist.id].rebooked = rebookingData.rebooked;
      // perStylistData[stylist.id].rebooking = (rebookingData.rebooked / rebookingData.total) * 100;
      perStylistData[stylist.id].rebooking = rebookingData.percentage * 100;
    }

    const frequencyData = visitFrequencyReport.by_stylist.find((freq) => freq.id === stylist.id);

    if (frequencyData) {
      perStylistData[stylist.id].frequency = frequencyData.visit_frequency.toFixed();
    }
  });

  const clientPerformanceTable = {
    rows: [],
    leftHeader: {
      inputFields: ['Staff'],
      styles: [],
    },
    topHeader: {
      inputFields: [
        'Client visits',
        'Clients rebooked',
        'Rebooking rate',
        'Client visit frequency (weeks)',
        'Client requests',
      ],
      styles: [],
    },
  };

  const sortedStylistGroup = sortArrayAccordingToAnotherArray(stylistList, Object.entries(rebookingReport.stylists));

  const keys = sortedStylistGroup.map((e) => e[0]);
  let salonTotalRequests = 0;
  keys.map((key) => {
    const aStylist = perStylistData[key];
    const rowToAdd = {
      inputFields: [],
      styles: [],
    };
    if (aStylist && requestsArr) {
      const requestsForStylist = requestsArr.filter((r) => r.username === aStylist.name);
      const requests = requestsForStylist.length > 0 ? requestsForStylist[0].requests_count : 0;
      salonTotalRequests += requests;
      // totalFrequency += aStylist.frequency;
      clientPerformanceTable.leftHeader && clientPerformanceTable.leftHeader.inputFields.push(aStylist.name);

      // rowToAdd.inputFields.push(stylist.visits);
      rowToAdd.inputFields.push(aStylist.total);
      rowToAdd.inputFields.push(aStylist.rebooked);
      rowToAdd.inputFields.push(fromPercentageToString(Math.round(aStylist.rebooking)));
      rowToAdd.inputFields.push(aStylist.frequency);
      rowToAdd.inputFields.push(requests.toString());

      clientPerformanceTable.rows.push(rowToAdd);
    }
    return null;
  });

  // Prepare the final row
  clientPerformanceTable.leftHeader && clientPerformanceTable.leftHeader.inputFields.push('Salon Total');
  // const clientVisitFrequency = keys.length !== 0 ? totalFrequency / keys.length : 0;
  const finalRow = {
    inputFields: [
      // totalVisits.toString(),
      rebookingReport.unique_visits.toString(),
      rebookingReport.rebooked_appointments.toString(),
      fromPercentageToString(rebookingReport.percentage * 100), // Rebooked,
      // parseFloat(clientVisitFrequency).toFixed(2),
      parseFloat(Math.round(visitFrequencyReport.total_frequency)).toFixed(),
      salonTotalRequests.toString(),
    ],
    styles: [],
  };
  clientPerformanceTable.rows.push(finalRow);
  return clientPerformanceTable;
};

export const generatePerformanceTable = (appointsReport, stylistIDInCalendarOrder) => {
  const performanceTable = {
    rows: [],
    leftHeader: {
      inputFields: ['Staff'],
      styles: [],
    },
    topHeader: {
      inputFields: [
        'Confirmed by SMS',
        'Value of confirmed',
        'Booked online',
        'Value of online',
        'Marked as no-show',
        'Value of No-shows',
        'Cancelled appointments',
        'Value of cancelled',
        'Value of cancelled/no-shows',
      ],
      styles: [
        'width-65', // use in print preview
        'width-60',
        'width-60',
        'width-60',
        'width-45',
        'width-65',
        'width-80',
        'width-65',
        'width-80',
      ],
    },
  };

  const totalConfirmed = appointsReport.sms_confirmed_count;
  const totalNcobCount = appointsReport.total_ncob_count;
  const totalNoShow = appointsReport.total_no_show_count;
  const totalCanceledAppointments = appointsReport.total_canceled_count;
  const totalCanceledValue = appointsReport.total_canceled_value;

  // let totalValue = 0;
  let totalConfirmedValue = 0;
  let totalNcobValue = 0;
  let totalNoshowValue = 0;

  const sortedStylistGroup = sortArrayAccordingToAnotherArray(
    stylistIDInCalendarOrder,
    Object.entries(appointsReport.stylist_group),
  );

  sortedStylistGroup.forEach((stylist, index) => {
    if (stylist[1].confirmed_value) {
      totalConfirmedValue += stylist[1].confirmed_value;
    }
    totalNcobValue += stylist[1].ncob_value;
    totalNoshowValue += stylist[1].no_show_value;

    const confirmedTotal = stylist[1].confirmed_value ? (
      <>
        <CurrencySign />
        {convertToMoneyAfterVat(stylist[1].confirmed_value)}
      </>
    ) : (
      <CurrencySign value="0" />
    );
    const confirmedCount = stylist[1].confirmed_count ? stylist[1].confirmed_count : 0;
    const canceledCount = stylist[1].canceled_count ? stylist[1].canceled_count : 0;
    const canceledValue = stylist[1].canceled_value ? stylist[1].canceled_value : 0;

    // TABLE COLUMNS
    performanceTable.rows[index] = {
      inputFields: [
        confirmedCount.toString(), // 'Confirmed by SMS',
        confirmedTotal, // 'Value of confirmed' — вече е JSX по-горе
        stylist[1].ncob_count, // 'Booked online',
        <>
          <CurrencySign />
          {convertToMoneyAfterVat(stylist[1].ncob_value)}
        </>,
        stylist[1].no_show_count, // 'Marked as no-show',
        <>
          <CurrencySign />
          {convertToMoneyAfterVat(stylist[1].no_show_value)}
        </>,
        canceledCount.toString(), // Cancelled appointments
        <>
          <CurrencySign />
          {convertToMoneyAfterVat(canceledValue)}
        </>,
        <>
          <CurrencySign />
          {convertToMoneyAfterVat(stylist[1].no_show_value + canceledValue)}
        </>,
      ],
      styles: ['', '', '', '', '', '', '', 'val-canceled ', ''],
    };

    // FOOTER
    performanceTable.rows.push({
      inputFields: [
        totalConfirmed.toString(), // Confirmed by SMS
        <>
          <CurrencySign />
          {convertToMoneyAfterVat(totalConfirmedValue)}
        </>, // Value of confirmed
        totalNcobCount.toString(), // Booked online
        <>
          <CurrencySign />
          {convertToMoneyAfterVat(totalNcobValue)}
        </>, // Value of online
        totalNoShow.toString(), // Marked as no-show
        <>
          <CurrencySign />
          {convertToMoneyAfterVat(totalNoshowValue)}
        </>, // Value of No-shows
        totalCanceledAppointments.toString(), // Cancelled appointments
        <>
          <CurrencySign />
          {convertToMoneyAfterVat(totalCanceledValue)}
        </>, // Value of cancelled
        <>
          <CurrencySign />
          {convertToMoneyAfterVat(totalNoshowValue + totalCanceledValue)}
        </>, // Value of cancelled/no-shows
      ],
      styles: ['', '', '', '', '', '', '', 'val-canceled ', ''],
    });

    const name = stylist[1].name;
    if (performanceTable.leftHeader && performanceTable.leftHeader.inputFields) {
      performanceTable.leftHeader.inputFields.push(name);
    }
  });
  if (performanceTable.leftHeader && performanceTable.leftHeader.inputFields) {
    performanceTable.leftHeader.inputFields.push('Salon total');
  }
  return performanceTable;
};

const getDataArray = (monthData, selectedStylist, currency) => {
  if (monthData) {
    const dataMonthArr = Object.entries(monthData);
    // there is a total in every month, add them together to get salonTotalTimeRange
    let salonTotalRevenueTimeRange = selectedStylist === STAFF_NAME.all ? D3.sum(dataMonthArr, (e) => e[1].total) : 0;
    let salonTotalServiceTimeRange = 0;
    let salonTotalRetailTimeRange = 0;
    let salonTotalNoshowTimeRange = 0;
    const dataArray = [];
    let salonTotalRevenue = 0;
    for (let i = 0; i < dataMonthArr.length; i += 1) {
      const monthStr = dataMonthArr[i][0];
      if (selectedStylist === STAFF_NAME.all) {
        salonTotalRevenue = dataMonthArr[i][1].total;
      } else if (selectedStylist === 'employed') {
        salonTotalRevenue = dataMonthArr[i][1].stylist_group.employed.total_revenue;
      } else if (selectedStylist === 'selfemployed') {
        salonTotalRevenue = dataMonthArr[i][1].stylist_group.selfemployed.total_revenue;
      } else {
        const arr = dataMonthArr[i][1].stylist_group.filter((s) => s.name === selectedStylist);
        if (arr.length > 0) {
          salonTotalRevenue = arr[0].total_revenue;
        } else {
          salonTotalRevenue = 0;
        }
      }
      let monthDataObj = {};
      const stylistGroup = dataMonthArr[i][1].stylist_group;
      let salonTotalService = 0;
      let salonTotalRetail = 0;
      let salonTotalNoshow = 0;

      for (let j = 0; j < stylistGroup.length; j += 1) {
        if (selectedStylist === STAFF_NAME.all) {
          salonTotalNoshow += stylistGroup[j].revenue_from_no_shows;
          salonTotalRetail += stylistGroup[j].revenue_from_products;
          salonTotalService += stylistGroup[j].revenue_from_services;
        } else if (stylistGroup[j].name === selectedStylist) {
          salonTotalRevenue = stylistGroup[j].total_revenue;
          salonTotalNoshow += stylistGroup[j].revenue_from_no_shows;
          salonTotalRetail += stylistGroup[j].revenue_from_products;
          salonTotalService = stylistGroup[j].revenue_from_services;
        }
      }
      salonTotalServiceTimeRange += salonTotalService;
      salonTotalNoshowTimeRange += salonTotalNoshow;
      salonTotalRetailTimeRange += salonTotalRetail;
      if (selectedStylist !== STAFF_NAME.all) {
        salonTotalRevenueTimeRange += salonTotalRevenue;
      }

      monthDataObj = {
        salonTotalService: salonTotalService.toFixed(2),
        salonTotalRetail: salonTotalRetail.toFixed(2),
        salonTotalNoshow: salonTotalNoshow.toFixed(2),
        salonTotalRevenue: salonTotalRevenue.toFixed(2),
      };
      dataArray.push({ [monthStr]: monthDataObj });
    }

    const totalTimeRange = {
      salonTotalRevenueTimeRange: formatMoney(salonTotalRevenueTimeRange, currency),
      salonTotalServiceTimeRange: formatMoney(salonTotalServiceTimeRange, currency),
      salonTotalRetailTimeRange: formatMoney(salonTotalRetailTimeRange, currency),
      salonTotalNoshowTimeRange: formatMoney(salonTotalNoshowTimeRange, currency),
    };
    return { dataArray, totalTimeRange };
  }
};

export const revenueByMonth = (monthData, selectedStylist) => {
  let shortendStylist = selectedStylist;
  if (selectedStylist === STAFF_NAME.employed) {
    shortendStylist = 'employed';
  } else if (selectedStylist === STAFF_NAME.self) {
    shortendStylist = 'selfemployed';
  }
  if (monthData && !monthData.errorMessage) {
    return getDataArray(monthData, shortendStylist);
  }
  if (monthData && monthData.errorMessage) {
    return monthData.errorMessage;
  }
};

export const makeMonthlyTable = (dataArray, whichTable, total, currency) => {
  let inputFields;
  const salonVatRate = STORE.getState().reports.salonVatRate;
  if (whichTable === 'Revenue') {
    inputFields = ['', 'Total Revenue (inc VAT)', 'Total Revenue (ex VAT)', 'Total VAT due'];
  } else if (whichTable === 'Service') {
    inputFields = ['', 'Service Revenue (inc VAT)', 'Service Revenue (ex VAT)', 'Service VAT due'];
  } else if (whichTable === 'Retail') {
    inputFields = ['', 'Retail Revenue (inc VAT)', 'Retail Revenue (ex VAT)', 'Retail VAT due'];
  } else if (whichTable === 'Noshow') {
    inputFields = ['', 'No-show Fees (inc VAT)', 'No-show Fees (ex VAT)', 'No-show VAT due'];
  }
  const sortedDataArray = [];
  const leftHeader = { inputFields, styles: [] };
  const rows = [
    { inputFields: [], styles: [] },
    { inputFields: [], styles: [] },
    { inputFields: [], styles: [] },
    { inputFields: [], styles: [] },
  ];
  if (dataArray && dataArray.length > 0) {
    const mons = Object.values(dataArray).map((e) => Object.keys(e)[0]);
    const f = (a, b) => {
      const strya = a.split(' ')[1];
      let strma = moment().month(a.split(' ')[0]).get('month') + 1;
      if (strma < 10) {
        strma = `0${strma}`;
      }
      const stryb = b.split(' ')[1];
      let strmb = moment().month(b.split(' ')[0]).get('month') + 1;
      if (strmb < 10) {
        strmb = `0${strmb}`;
      }
      if (moment(`${strya}${strma}01`).isBefore(moment(`${stryb}${strmb}01`))) {
        return -1;
      }
      return 1;
    };
    mons.sort(f);
    for (let i = 0; i < mons.length; i += 1) {
      sortedDataArray.push(dataArray.find((e) => Object.keys(e)[0] === mons[i]));
    }
    rows[0].inputFields = mons;
    rows[0].styles = [];
    rows[0].inputFields.push('TOTAL');
    rows[1].inputFields = Object.values(sortedDataArray).map((e) =>
      formatMoney(Object.values(e)[0][`salonTotal${whichTable}`], currency),
    );
    rows[1].inputFields.push(total);

    rows[1].styles = [];
    rows[2].inputFields = Object.values(sortedDataArray).map((e) =>
      formatMoney(parseFloat(Object.values(e)[0][`salonTotal${whichTable}`]) / (1 + salonVatRate / 100), currency),
    );
    const afterVatTotal = formatMoney(fromMoneyStringToNumber(total) / (1 + salonVatRate / 100), currency);
    rows[2].inputFields.push(afterVatTotal);
    rows[2].styles = [];
    rows[3].inputFields = Object.values(sortedDataArray).map((e) =>
      formatMoney(
        parseFloat(Object.values(e)[0][`salonTotal${whichTable}`]) * (1 - 1 / (1 + salonVatRate / 100)),
        currency,
      ),
    );
    const vatDueTotal = formatMoney(fromMoneyStringToNumber(total) * (1 - 1 / (1 + salonVatRate / 100)), currency);
    rows[3].inputFields.push(vatDueTotal);

    rows[3].styles = [];
  }
  // make chartData
  const chartData = [];
  const monthNames = rows[0].inputFields;
  for (let i = 0; i < monthNames.length - 1; i += 1) {
    const monthName = window.innerWidth < 600 ? monthNames[i].substr(0, 3) : monthNames[i];
    chartData.push({
      name: monthName,
      total: parseFloat(Object.values(sortedDataArray[i])[0][`salonTotal${whichTable}`]),
    });
  }
  //
  return { leftHeader, rows, chartData };
};
